import {ALL_WEEKDAY_INDICES} from '@/mixins/weekdays';

export function addOneDayToDate(date) {
  let dateCopy = new Date(date.valueOf());
  dateCopy.setDate(dateCopy.getDate() + 1);
  dateCopy.setHours(0, 0, 0, 0);
  return dateCopy;
}

/**
 * Return date as string like 'YYYY-MM-DD'.
 */
export function formatDate(date) {
  // This formatting is taken from the docs:
  // https://vuetifyjs.com/components/pickers#api
  if (typeof date === 'object') {
    // Include time zone offset otherwise the actual date may change.
    // See: https://stackoverflow.com/a/37661393
    const isoDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    ).toISOString();
    date = isoDate;
  }
  return date.substr(0, 10);
}

export function subtractOneDayFromDate(date) {
  const dateCopy = new Date(date.valueOf());
  dateCopy.setDate(dateCopy.getDate() - 1);
  dateCopy.setHours(0, 0, 0, 0);
  return dateCopy;
}

/**
 * Safari doesn't understand '2018-01-18' as a Date format, so to
 * make this work on iOS we need to replace dashes with slashes.
 * This format should also work in all other browsers.
 *
 * See: https://stackoverflow.com/questions/4310953
 */
function fixDateStringForSafari(dateString) {
  const fixedDateString = dateString.replace(/-/g, '/');
  return fixedDateString;
}

/**
 * Return whether date1 is before date2.
 */
export function isBefore(dateString1, dateString2) {
  const fixedDateString1 = fixDateStringForSafari(dateString1);
  const fixedDateString2 = fixDateStringForSafari(dateString2);

  const date1 = new Date(fixedDateString1);
  const date2 = new Date(fixedDateString2);

  // Ignore time, only compare day.
  date1.setHours(0, 0, 0, 0);
  date2.setHours(0, 0, 0, 0);

  return date1 < date2;
}

/**
 * A mixin to handle start- and stopdates.
 *
 * Used in views where the User can pick a timing, e.g. for a SpotEntry.
 *
 * Partially depends on:
 *
 *   - this.spot
 *   - this.entries
 *   - this.firstEntry
 */
const timing = {
  data() {
    return {
      // These dates are strings, not Date objects, since the v-date-
      // picker widgets only deal with strings. Any calculation will
      // first have to cast them to Date.
      startDate: null,
      stopDate: null,

      // Weekdays are represented as weekday indices starting at Monday (0).
      weekDays: ALL_WEEKDAY_INDICES,
    };
  },
  computed: {
    /**
     * The date is pulled from this.spot or this.entries, if available.
     *
     * Otherwise we return undefined.
     */
    hardStartDateString() {
      if (this.spot && this.spot.hard_start) {
        const date = new Date(this.spot.hard_start);
        return this.formatDate(date);
      } else if (this.entries) {
        if (
          this.firstEntry &&
          this.firstEntry.spot &&
          this.firstEntry.spot.hard_start
        ) {
          const date = new Date(this.firstEntry.spot.hard_start);
          return this.formatDate(date);
        }
      }
      return undefined;
    },
    /**
     * The date is pulled from this.spot or this.entries, if available.
     *
     * Otherwise we return undefined.
     */
    hardExpiryDateString() {
      if (this.spot && this.spot.hard_expire) {
        const date = new Date(this.spot.hard_expire);
        return this.formatDate(subtractOneDayFromDate(date));
      } else if (this.entries) {
        if (
          this.firstEntry &&
          this.firstEntry.spot &&
          this.firstEntry.spot.hard_expire
        ) {
          const date = new Date(this.firstEntry.spot.hard_expire);
          return this.formatDate(subtractOneDayFromDate(date));
        }
      }
      return undefined;
    },
    stopDatePlaceHolder() {
      if (this.stopDate) {
        return this.stopDate;
      }
      if (this.hardExpiryDateString) {
        return this.hardExpiryDateString;
      }
      return this.$t('toUnlimited');
    },
    showLastInitialDatesChip() {
      const dataStored = this.$store.getters.lastInitialDates;
      if (!dataStored) {
        return false;
      }
      const startDateString = this.formatDate(
        this.$store.getters.lastInitialDates.start
      );
      const stopDateString = this.formatDate(
        this.$store.getters.lastInitialDates.stop
      );
      return (
        this.isAllowedStartDateWithinHardRange(startDateString) &&
        this.isAllowedStopDateWithinHardExpiry(stopDateString)
      );
    },
  },
  watch: {
    /** Unset stopDate when new startDate invalidates it. */
    startDate: function (startDate) {
      if (startDate && this.stopDate) {
        if (this._isBefore(this.stopDate, startDate)) {
          this.stopDate = '';
        }
      }
    },
    /** Unset startDate when new stopDate invalidates it. */
    stopDate: function (stopDate) {
      if (this.startDate && stopDate) {
        if (this._isBefore(stopDate, this.startDate)) {
          // Note: It is important we set an empty string here instead
          //   of null, as the backend will then use 'today' during
          //   entry-updates instead of doing nothing, which may result
          //   in the old startdate being invalid with the new stopdate.
          this.startDate = '';
        }
      }
    },
  },
  methods: {
    setInitialDates(startDate, stopDate) {
      this.startDate = this.formatDate(startDate);
      this.stopDate = this.formatDate(stopDate);
    },
    addOneDayToDate: addOneDayToDate,
    subtractOneDayFromDate: subtractOneDayFromDate,
    /** Get Date object or string and return a formatted string. */
    formatDate: formatDate,
    fixDateStringForSafari: fixDateStringForSafari,
    _isBefore(dateString1, dateString2) {
      return isBefore(dateString1, dateString2);
    },
    _isBeforeOrSameAs(dateString1, dateString2) {
      const fixedDateString1 = this.fixDateStringForSafari(dateString1);
      const fixedDateString2 = this.fixDateStringForSafari(dateString2);

      const date1 = new Date(fixedDateString1);
      const date2 = new Date(fixedDateString2);

      // Ignore time, only compare day.
      date1.setHours(0, 0, 0, 0);
      date2.setHours(0, 0, 0, 0);

      return date1 <= date2;
    },
    _isAfter(dateString1, dateString2) {
      const fixedDateString1 = this.fixDateStringForSafari(dateString1);
      const fixedDateString2 = this.fixDateStringForSafari(dateString2);

      const date1 = new Date(fixedDateString1);
      const date2 = new Date(fixedDateString2);

      // Ignore time, only compare day.
      date1.setHours(0, 0, 0, 0);
      date2.setHours(0, 0, 0, 0);

      return date1 > date2;
    },
    _isAfterOrSameAs(dateString1, dateString2) {
      const fixedDateString1 = this.fixDateStringForSafari(dateString1);
      const fixedDateString2 = this.fixDateStringForSafari(dateString2);

      const date1 = new Date(fixedDateString1);
      const date2 = new Date(fixedDateString2);

      // Ignore time, only compare day.
      date1.setHours(0, 0, 0, 0);
      date2.setHours(0, 0, 0, 0);

      return date1 >= date2;
    },
    isNotInPast(dateString) {
      dateString = this.fixDateStringForSafari(dateString);
      const date = new Date(dateString);

      const now = new Date();
      const yesterday = now.setDate(now.getDate() - 1);
      const isPast = date < yesterday;
      return !isPast;
    },
    isInPast(dateString) {
      return !this.isNotInPast(dateString);
    },
    isNotInFuture(dateString) {
      dateString = this.fixDateStringForSafari(dateString);
      const date = new Date(dateString);

      const today = new Date();
      const isFuture = date > today;
      return !isFuture;
    },
    /**
     * Check whether given date is:
     *   - >= hardStart
     *   - <= hardExpire
     *   - >= today
     */
    isAllowedStartDateWithinHardRange(dateString) {
      let withinHardRange = true;

      if (this.hardStartDateString) {
        const beforeHardStart = !this._isAfterOrSameAs(
          dateString,
          this.hardStartDateString
        );
        if (beforeHardStart) {
          withinHardRange = false;
        }
      }

      if (this.hardExpiryDateString) {
        const hardExpired = !this._isBeforeOrSameAs(
          dateString,
          this.hardExpiryDateString
        );
        if (hardExpired) {
          withinHardRange = false;
        }
      }

      return this.isNotInPast(dateString) && withinHardRange;
    },
    /**
     * Check whether given date >= today and <= hardExpiryDate.
     */
    isAllowedStopDateWithinHardExpiry(dateString) {
      let withinHardExpiration = true;
      if (this.hardExpiryDateString) {
        withinHardExpiration = this._isBeforeOrSameAs(
          dateString,
          this.hardExpiryDateString
        );
      }
      return this.isNotInPast(dateString) && withinHardExpiration;
    },
    /**
     * Return whether given date is within chosen daterange.
     *
     * Can be used for the :events prop on v-date-picker.
     */
    isEventForChosenDateRange(date) {
      if (!this.isNotInPast(date)) {
        return false;
      }
      const start = this.startDate || this.hardStartDateString;
      const stop = this.stopDate || this.hardExpiryDateString;
      if (!start && !stop) {
        return false;
      }
      if (start && date < start) {
        return false;
      }
      if (stop && date > stop) {
        return false;
      }
      return true;
    },
    resetDates() {
      this.startDate = null;
      this.stopDate = null;
    },
    /**
     * Remember current daterange as Date objects in store.
     */
    rememberLastInitialDates() {
      if (this.startDate && this.stopDate) {
        const startDate = this.formatDate(this.startDate);
        const stopDate = this.formatDate(this.stopDate);

        this.$store.commit('SET_LAST_INITIAL_DATES', {
          start: startDate,
          stop: stopDate,
        });
      }
    },
    /**
     * Remove daterange from store by setting the dates to null.
     */
    forgetLastInitialDates() {
      this.$store.commit('SET_LAST_INITIAL_DATES', {
        start: null,
        stop: null,
      });
    },
  },
};

export default timing;



// WEBPACK FOOTER //
// ./src/mixins/timing.js